import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { deepStripUndefined } from '../utils/dataSanitizer';
import './SignupSuccess.css';

const SignupSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    const handleSuccessfulPayment = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const sessionId = urlParams.get('session_id');
        
        if (!sessionId) {
          throw new Error('No session ID found');
        }

        // Retrieve session details from Stripe (you'd typically do this on your backend)
        console.log('Payment successful for session:', sessionId);
        
        // Get user data from localStorage (set during checkout)
        let checkoutData = localStorage.getItem('bullbearly-checkout-data');
        console.log('Retrieved checkout data from localStorage:', checkoutData);
        
        // If not found in localStorage, try sessionStorage as backup
        if (!checkoutData) {
          checkoutData = sessionStorage.getItem('bullbearly-checkout-data');
          console.log('Retrieved checkout data from sessionStorage:', checkoutData);
        }
        
        if (!checkoutData) {
          // Check if there's any user data in localStorage as fallback
          const fallbackUserData = localStorage.getItem('bullbearly-user');
          console.log('No checkout data found, checking for fallback user data:', fallbackUserData);
          
          if (fallbackUserData) {
            console.log('User already exists, payment was successful but account already created');
            setSessionData({
              sessionId,
              subscriptionType: 'MANAGED', // Default since we don't know the exact type
              email: 'user@example.com'
            });
            setLoading(false);
            
            // Redirect to dashboard after 3 seconds
            setTimeout(() => {
              navigate('/news');
            }, 3000);
            return;
          }
          
          throw new Error('Checkout data not found. Your payment was successful, but there was an issue retrieving your account details. Please contact support or try logging in with your credentials.');
        }

        const userData = JSON.parse(checkoutData);
        console.log('Creating user account from checkout data:', userData);

        // Check if user already exists to prevent duplicates
        const { getUserByEmail } = await import('../services/userService');
        const existingUser = await getUserByEmail(userData.email);
        
        if (existingUser) {
          console.log('User already exists, skipping creation:', existingUser.email);
          setSessionData({
            sessionId,
            subscriptionType: existingUser.subscriptionType,
            email: existingUser.email
          });
          setLoading(false);
          
          // Clear checkout data
          localStorage.removeItem('bullbearly-checkout-data');
          sessionStorage.removeItem('bullbearly-checkout-data');
          
          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/news');
          }, 3000);
          return;
        }

        // Fetch session details to get payment information
        let paymentInfo = null;
        try {
          const stripeService = (await import('../services/stripeService')).default;
          const sessionDetails = await stripeService.getSessionDetails(sessionId);
          if (sessionDetails) {
            paymentInfo = {
              sessionId: sessionId,
              subscriptionId: sessionDetails.subscription?.id,
              customerId: sessionDetails.customer,
              paymentIntentId: sessionDetails.payment_intent_id, // Use the new field name from server
              amount: sessionDetails.amount_total,
              currency: sessionDetails.currency,
              paymentStatus: sessionDetails.payment_status,
              subscriptionStatus: sessionDetails.subscription?.status,
              createdAt: new Date().toISOString(),
              lastPaymentDate: new Date().toISOString()
            };
          }
        } catch (error) {
          console.error('Error fetching session details:', error);
        }

        // Create user account with the selected subscription type and payment info
        const userDataWithSubscription = {
          ...userData,
          subscriptionType: userData.subscriptionType || 'MANAGED', // Default to MANAGED if not set
          paymentInfo: paymentInfo
        };

        // Sanitize data to remove undefined values before saving to Firestore
        const sanitizedUserData = deepStripUndefined(userDataWithSubscription);
        
        console.log('Creating new user account:', sanitizedUserData);
        // Create user account
        await signup(sanitizedUserData);
        
        // Clear checkout data from both storage locations
        localStorage.removeItem('bullbearly-checkout-data');
        sessionStorage.removeItem('bullbearly-checkout-data');
        
        setSessionData({
          sessionId,
          subscriptionType: userData.subscriptionType,
          email: userData.email
        });
        
        setLoading(false);
        
        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/news');
        }, 3000);
        
      } catch (error) {
        console.error('Error handling successful payment:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    handleSuccessfulPayment();
  }, [location, navigate, signup]);

  if (loading) {
    return (
      <div className="signup-success-container">
        <div className="success-modal">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
          <h2>Processing Your Payment...</h2>
          <p>Please wait while we set up your account.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="signup-success-container">
        <div className="success-modal error">
          <div className="error-icon">❌</div>
          <h2>Payment Processing Error</h2>
          <p>{error}</p>
          
          <div className="error-actions">
            <button 
              className="retry-button"
              onClick={() => navigate('/')}
            >
              Return to Home
            </button>
            <button 
              className="login-button"
              onClick={() => {
                // Clear any existing user data and go to login
                localStorage.removeItem('bullbearly-user');
                localStorage.removeItem('bullbearly-checkout-data');
                sessionStorage.removeItem('bullbearly-checkout-data');
                navigate('/');
                // Trigger login modal
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('openLoginModal'));
                }, 100);
              }}
            >
              Try Logging In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-success-container">
      <div className="success-modal">
        <div className="success-icon">✅</div>
        <h2>Welcome to BullBearly!</h2>
        <p>Your payment has been processed successfully.</p>
        
        {sessionData && (
          <div className="subscription-details">
            <h3>Subscription Details</h3>
            <div className="detail-row">
              <span>Plan:</span>
              <span>{sessionData.subscriptionType === 'MANAGED' ? 'Early Bird - Managed' : 'Early Bird - BYOK'}</span>
            </div>
            <div className="detail-row">
              <span>Email:</span>
              <span>{sessionData.email}</span>
            </div>
            <div className="detail-row">
              <span>Session ID:</span>
              <span className="session-id">{sessionData.sessionId}</span>
            </div>
          </div>
        )}
        
        <div className="success-message">
          <p>Your account has been created and you're being redirected to the dashboard...</p>
          <div className="countdown">Redirecting in 3 seconds...</div>
        </div>
        
        <button 
          className="continue-button"
          onClick={() => navigate('/news')}
        >
          Go to Dashboard Now
        </button>
      </div>
    </div>
  );
};

export default SignupSuccess;
